import { hasPaidMembershipAccess } from '@/utils/membership-access';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const APP_MEMBERSHIP_SUBSCRIPTION_NOS: readonly number[] = [21, 22, 23];
const WEB_MEMBERSHIP_SUBSCRIPTION_NOS: readonly number[] = [24, 25];
export const MOBILE_MEMBERSHIP_CPLAT_TOKEN_STORAGE_KEY = 'mobileMembershipCplatToken';

type MembershipSubscriptionChannel = 'APP' | 'WEB' | 'UNKNOWN';

const SUBSCRIPTION_NO_KEYS = [
  'SubscriptionNo',
  'subscriptionNo',
  'SubNo',
  'subNo',
  'Sub_No',
  'sub_no',
  'SubscriptionCode',
  'subscriptionCode',
  'CurrentSubscriptionNo',
  'currentSubscriptionNo',
];

export const hasStoredCplatToken = () => {
  return Boolean(getStoredCplatToken());
};

export const getStoredCplatToken = () => {
  if (typeof localStorage === 'undefined') return '';

  const localStorageToken = normalizeCplatToken(localStorage.getItem('cplatToken'));
  if (localStorageToken) return localStorageToken;

  if (typeof sessionStorage === 'undefined') return '';

  return normalizeCplatToken(sessionStorage.getItem(MOBILE_MEMBERSHIP_CPLAT_TOKEN_STORAGE_KEY));
};

export const resolveMobileMembershipCplatToken = (
  reduxToken?: string | null,
  queryStringToken?: string | null,
) => {
  const normalizedQueryToken = normalizeCplatToken(queryStringToken);
  if (normalizedQueryToken) return normalizedQueryToken;

  const normalizedReduxToken = normalizeCplatToken(reduxToken);
  const storedToken = getStoredCplatToken();

  if (!storedToken) return normalizedReduxToken;
  if (!normalizedReduxToken) return storedToken;
  if (storedToken === normalizedReduxToken) return normalizedReduxToken;

  return isLikelyJwtToken(storedToken) ? storedToken : normalizedReduxToken;
};

export const getCanUseMobileMembershipReduxUser = (
  reduxToken?: string | null,
  queryStringToken?: string | null,
) => {
  const normalizedReduxToken = normalizeCplatToken(reduxToken);
  if (!normalizedReduxToken) return false;

  const explicitToken = normalizeCplatToken(queryStringToken) || getJwtStoredCplatToken();

  return !explicitToken || explicitToken === normalizedReduxToken;
};

export const normalizeCplatToken = (token?: string | null) => {
  const trimmedToken = token?.trim();
  if (!trimmedToken) return '';

  try {
    const parsedToken: unknown = JSON.parse(trimmedToken);
    if (typeof parsedToken === 'string') return parsedToken.trim();
  } catch {
    // Non-JSON legacy values are normalized below.
  }

  return trimmedToken.replace(/^['"]|['"]$/g, '').trim();
};

export const getIsAnySubscribed = (serviceInfo: unknown) => {
  return hasPaidMembershipAccess(serviceInfo);
};

export const getMembershipSubscriptionNo = (serviceInfo: unknown) => {
  if (!isRecord(serviceInfo)) return null;

  const appSellerInfo = serviceInfo.AppSellerInfo;
  if (!isRecord(appSellerInfo)) return null;

  const subscriptionNo = readSubscriptionNo(appSellerInfo);
  if (subscriptionNo !== null) return subscriptionNo;

  const nestedRecord = Object.values(appSellerInfo).find(isRecord);
  return nestedRecord ? readSubscriptionNo(nestedRecord) : null;
};

export const getMembershipSubscriptionChannel = (
  serviceInfo: unknown,
): MembershipSubscriptionChannel => {
  const subscriptionNo = getMembershipSubscriptionNo(serviceInfo);
  if (subscriptionNo === null) return 'UNKNOWN';
  if (APP_MEMBERSHIP_SUBSCRIPTION_NOS.includes(subscriptionNo)) return 'APP';
  if (WEB_MEMBERSHIP_SUBSCRIPTION_NOS.includes(subscriptionNo)) return 'WEB';

  return 'UNKNOWN';
};

export const getIsAppMembershipSubscribed = (serviceInfo: unknown) => {
  return getIsAnySubscribed(serviceInfo) && getMembershipSubscriptionChannel(serviceInfo) === 'APP';
};

export const getIsWebMembershipSubscribed = (serviceInfo: unknown) => {
  return getIsAnySubscribed(serviceInfo) && getMembershipSubscriptionChannel(serviceInfo) === 'WEB';
};

const readNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return null;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const readSubscriptionNo = (record: Record<string, unknown>) => {
  return (
    SUBSCRIPTION_NO_KEYS.map((key) => readNumber(record[key])).find(
      (value): value is number => value !== null,
    ) ?? null
  );
};

const getJwtStoredCplatToken = () => {
  const storedToken = getStoredCplatToken();

  return isLikelyJwtToken(storedToken) ? storedToken : '';
};

const isLikelyJwtToken = (token: string) => {
  return token.split('.').length === 3;
};
